import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, PlayArrow } from '@mui/icons-material';
import apiClient from '../../services/api';

interface ProgressStage {
  id: number;
  stage: string;
  status: string;
  employee_id?: number;
}

interface OrderProgress {
  order_id: number;
  stages: ProgressStage[];
}

interface OrderProgressDialogProps {
  open: boolean;
  onClose: () => void;
  orderId: number;
  orderNumber: string;
}

const OrderProgressDialog: React.FC<OrderProgressDialogProps> = ({
  open,
  onClose,
  orderId,
  orderNumber
}) => {
  const [progress, setProgress] = useState<OrderProgress | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableStages, setAvailableStages] = useState<Array<{value: string, label: string}>>([]);

  useEffect(() => {
    if (open) {
      fetchProgress();
      fetchAvailableStages();
    }
  }, [open, orderId]);

  const fetchProgress = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/progress/order/${orderId}`);
      setProgress(response.data);
    } catch (err) {
      console.error('Error fetching progress data:', err);
      setError('Failed to fetch progress data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableStages = async () => {
    try {
      const response = await apiClient.get('/progress/stages');
      setAvailableStages(response.data.stages || []);
    } catch (err) {
      console.error('Error fetching available stages:', err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'on_hold':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleInitializeProgress = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post(`/progress/order/${orderId}/initialize`);
      if (response.status === 200) {
        // Refresh progress data after initialization
        await fetchProgress();
      }
    } catch (err) {
      console.error('Error initializing progress:', err);
      setError('Failed to initialize progress tracking');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStageStatus = async (stage: string, status: string) => {
    try {
      const response = await apiClient.put(`/progress/order/${orderId}/stage/${stage}`, {
        status: status
      });
      if (response.status === 200) {
        // Refresh progress data after update
        await fetchProgress();
      }
    } catch (err) {
      console.error('Error updating stage status:', err);
      setError('Failed to update stage status');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'in_progress':
        return <PlayArrow color="primary" />;
      default:
        return <RadioButtonUnchecked color="disabled" />;
    }
  };

  const renderProgressContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!progress || progress.stages.length === 0) {
      return (
        <Box p={3}>
          <Typography variant="body1" color="text.secondary">
            No progress tracking data available for this order.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Available manufacturing stages:
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {availableStages.map((stage) => (
              <Chip
                key={stage.value}
                label={stage.label}
                variant="outlined"
                size="small"
              />
            ))}
          </Box>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleInitializeProgress}
              disabled={loading}
            >
              Initialize Progress Tracking
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box p={2}>
        <Typography variant="h6" gutterBottom>
          Manufacturing Progress for Order #{orderNumber}
        </Typography>
        
        <Stepper orientation="vertical">
          {progress.stages.map((stage, index) => (
            <Step key={stage.id} active={true}>
              <StepLabel
                icon={getStatusIcon(stage.status)}
                optional={
                  <Chip
                    label={stage.status.replace('_', ' ').toUpperCase()}
                    color={getStatusColor(stage.status) as any}
                    size="small"
                  />
                }
              >
                {stage.stage.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Status: {stage.status.replace('_', ' ')}
                  {stage.employee_id && (
                    <span> • Assigned to Employee ID: {stage.employee_id}</span>
                  )}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {stage.status === 'not_started' && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => handleUpdateStageStatus(stage.stage, 'in_progress')}
                    >
                      Start
                    </Button>
                  )}
                  {stage.status === 'in_progress' && (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        onClick={() => handleUpdateStageStatus(stage.stage, 'completed')}
                      >
                        Complete
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={() => handleUpdateStageStatus(stage.stage, 'on_hold')}
                      >
                        Hold
                      </Button>
                    </>
                  )}
                  {stage.status === 'on_hold' && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => handleUpdateStageStatus(stage.stage, 'in_progress')}
                    >
                      Resume
                    </Button>
                  )}
                  {stage.status === 'completed' && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      onClick={() => handleUpdateStageStatus(stage.stage, 'in_progress')}
                    >
                      Reopen
                    </Button>
                  )}
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          backgroundColor: 'surface',
          border: '1px solid',
          borderColor: 'outlineVariant',
          boxShadow: 3,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: 'surfaceContainer',
          color: 'onSurface',
          fontWeight: 500,
          fontSize: '1.5rem',
          py: 3,
          px: 4,
          borderBottom: '1px solid',
          borderColor: 'outlineVariant',
        }}
      >
        Order Progress Tracking
      </DialogTitle>
      <DialogContent
        sx={{
          backgroundColor: 'surface',
          py: 2,
          px: 2,
        }}
      >
        {renderProgressContent()}
      </DialogContent>
      <DialogActions
        sx={{
          backgroundColor: 'surfaceContainer',
          borderTop: '1px solid',
          borderColor: 'outlineVariant',
          p: 4,
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            borderRadius: 7,
            backgroundColor: 'primary.main',
            color: 'onPrimary',
            fontWeight: 500,
            px: 4,
            py: 1.5,
            textTransform: 'none',
            fontSize: '1rem',
            boxShadow: 2,
            '&:hover': {
              backgroundColor: 'primary.dark',
              boxShadow: 4,
            },
            '&:active': {
              boxShadow: 1,
            },
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderProgressDialog;
